import { useEffect } from 'react';
import { useParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useReport } from '../hooks/useReport';
import { useLanguage } from '../contexts/LanguageContext';
import ErrorBoundary from '../components/ErrorBoundary';
import { dqRuleService } from '../services/dqRuleService';
import { useState, useCallback } from 'react';
import { useToast } from '../hooks/useToast';
import { useTranslate } from '../hooks/useTranslate';
import { VscFeedback } from 'react-icons/vsc';
import { IoLanguage } from 'react-icons/io5';
import { downloadReportAsPDF } from '../utils/reportDownload';
import { HiShare } from 'react-icons/hi2';
import { IoIosCloudDownload } from 'react-icons/io';
import { sharedReportService } from '../services/sharedReportService';
import FeedbackModal from '../components/FeedbackModal';
import LanguageSelectorModal from '../components/LanguageSelectorModal';
import ContextualHelpLink from '../components/training/ContextualHelpLink';

import { ReportErrorPanel } from '../components/report/ReportErrorPanel';
import { CinematicThemeProvider } from '../contexts/CinematicThemeContext';
import { ReportThemeProvider, useReportTheme } from '../contexts/ReportThemeContext';
import { CinematicExecutiveSummary } from '../components/report/CinematicExecutiveSummary';
import { CinematicHealthAndDimensions } from '../components/report/CinematicHealthAndDimensions';
import { CinematicProblemSolutions } from '../components/report/CinematicProblemSolutions';


interface NewReportPageProps {
    datasetId?: string;
}

interface LocationState {
    dataObjectName?: string;
    dataObjectUuid?: string;
    /** Legacy field for backward compatibility with mock data */
    dataObjectId?: number;
    /** URL to return to when navigating back (preserves folder context) */
    returnUrl?: string;
    /** Optional list of selected columns for a scoped report */
    selectedColumns?: string[];
}

export function NewReportPage({ datasetId: propDatasetId }: NewReportPageProps) {
    return (
        <ReportThemeProvider defaultTheme="gradient-steel">
            <NewReportPageContent datasetId={propDatasetId} />
        </ReportThemeProvider>
    );
}

function NewReportPageContent({ datasetId: propDatasetId }: NewReportPageProps) {
    const { getThemeClasses } = useReportTheme();
    const themeClasses = getThemeClasses();
    const { datasetId: routeDatasetId } = useParams<{ datasetId: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const datasetId = propDatasetId || routeDatasetId || 'default';

    // Parse query parameters
    const queryDataObjectId = searchParams.get('dataObjectId');
    const queryDataObjectUuid = searchParams.get('dataObjectUuid');
    const queryDataObjectName = searchParams.get('dataObjectName');
    const queryReportId = searchParams.get('reportId'); // For historical reports
    const returnUrl = searchParams.get('returnUrl'); // For preserving folder context
    const queryUserEmail = searchParams.get('userEmail'); // For admin viewing other users' reports

    // Get data object context from route state (legacy support)
    const state = location.state as LocationState | null;
    const stateDataObjectName = state?.dataObjectName;
    const stateDataObjectUuid = state?.dataObjectUuid;
    const stateSelectedColumns = state?.selectedColumns || [];
    const stateReturnUrl = state?.returnUrl;

    // Use query parameter if available, otherwise fall back to state
    const dataObjectUuid = queryDataObjectUuid || queryDataObjectId || stateDataObjectUuid;
    const dataObjectName = queryDataObjectName || stateDataObjectName;
    const finalReturnUrl = returnUrl || stateReturnUrl || '/datasources';

    // Handle navigation back with proper folder context
    const handleBackNavigation = () => {
        navigate(finalReturnUrl, { replace: true });
    };

    // Handle browser back button to preserve folder context
    useEffect(() => {
        const handlePopState = (event: PopStateEvent) => {
            // If user clicks browser back, redirect to proper folder context
            if (finalReturnUrl !== '/datasources') {
                event.preventDefault();
                navigate(finalReturnUrl, { replace: true });
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [finalReturnUrl, navigate]);

    const reportId = queryReportId || undefined; // For historical reports - convert null to undefined

    const { activeLanguage } = useLanguage();
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const [showLanguageModal, setShowLanguageModal] = useState(false);

    const { report, loading, error, refresh, translationStats } = useReport({
        dataObjectUuid,
        reportId,
        datasetId: datasetId || 'default',
        dataObjectName,
        ownerEmail: queryUserEmail || undefined,
        targetLanguage: activeLanguage,
    });

    // If a reportId fails with "not found", it might be a shared report link
    // (someone copied /report?reportId=... instead of using the Share button).
    // Redirect to /shared-report to try the sharing endpoint.
    useEffect(() => {
        if (error && reportId && !loading) {
            const msg = error.message.toLowerCase();
            if (msg.includes('not found') || msg.includes('not_found')) {
                navigate(`/shared-report?reportId=${encodeURIComponent(reportId)}`, { replace: true });
            }
        }
    }, [error, reportId, loading, navigate]);

    const displayReport = report;

    // State for user-prescribed rules data
    const [userRulesData, setUserRulesData] = useState<any>(null);
    const [rulesLoading, setRulesLoading] = useState(false);
    const [hasShownAiDisclaimer, setHasShownAiDisclaimer] = useState(false);

    const { push: pushToast } = useToast();
    const { t } = useTranslate();

    const [isExporting, setIsExporting] = useState(false);

    const handleDownloadPDF = async () => {
        setIsExporting(true);
        pushToast({ variant: 'info', message: t('Generating PDF — please wait...'), ttl: 6000 });
        try {
            const success = await downloadReportAsPDF(report?.dataset?.name || 'report');
            if (success) {
                pushToast({ variant: 'success', message: t('PDF downloaded successfully'), ttl: 5000 });
            } else {
                pushToast({ variant: 'error', message: t('Failed to generate PDF. Please try again.'), ttl: 6000 });
            }
        } catch {
            pushToast({ variant: 'error', message: t('Failed to generate PDF'), ttl: 5000 });
        } finally {
            setIsExporting(false);
        }
    };

    const [isSharing, setIsSharing] = useState(false);
    const [showSharePopup, setShowSharePopup] = useState(false);
    const [shareUrl, setShareUrl] = useState('');
    const [linkCopied, setLinkCopied] = useState(false);

    const handleShareLink = useCallback(async () => {
        const currentReportId = reportId || queryReportId;
        const url = currentReportId
            ? `${window.location.origin}/shared-report?reportId=${currentReportId}`
            : window.location.href;

        setIsSharing(true);
        try {
            if (currentReportId) {
                await sharedReportService.createShareLink(currentReportId);
            }
            setShareUrl(url);
            setLinkCopied(false);
            setShowSharePopup(true);
        } catch {
            pushToast({ variant: 'error', message: t('Failed to create share link'), ttl: 4000 });
        } finally {
            setIsSharing(false);
        }
    }, [reportId, queryReportId, pushToast, t]);

    const handleCopyShareLink = useCallback(() => {
        navigator.clipboard.writeText(shareUrl).then(() => {
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2500);
        }).catch(() => {
            pushToast({ variant: 'error', message: t('Failed to copy link'), ttl: 4000 });
        });
    }, [shareUrl, pushToast, t]);

    // Show AI disclaimer toast when report loads successfully
    useEffect(() => {
        if (report && !loading && !error && !hasShownAiDisclaimer) {
            setHasShownAiDisclaimer(true);

            pushToast({
                variant: 'error', // Red color as requested
                title: t('AI-Generated Report'), // Bold header
                message: t('This analysis is generated by artificial intelligence. Please review findings carefully.'),
                ttl: 6000, // 6 seconds auto-dismiss
                hideIcon: true // Hide the left icon to avoid confusion
            });
        }
    }, [report, loading, error, hasShownAiDisclaimer, pushToast]);

    // Show toast on translation success
    useEffect(() => {
        if (translationStats && activeLanguage !== 'en') {
            pushToast({
                variant: 'info',
                message: `Translated ${translationStats.fields_translated} fields in ${translationStats.duration_seconds.toFixed(1)}s`,
                ttl: 4000,
            });
        }
    }, [translationStats, activeLanguage, pushToast]);

    // Extract dq_framework_results from report when available (historical reports)
    useEffect(() => {
        console.log('[DEBUG] NewReportPage useEffect - report:', report);
        console.log('[DEBUG] NewReportPage useEffect - report keys:', report ? Object.keys(report) : 'no report');
        console.log('[DEBUG] NewReportPage useEffect - reportId:', reportId);
        console.log('[DEBUG] NewReportPage useEffect - dataObjectUuid:', dataObjectUuid);

        if (report && (report as any).dq_framework_results) {
            console.log('[DEBUG] Found dq_framework_results in report:', (report as any).dq_framework_results);
            setUserRulesData((report as any).dq_framework_results);
            console.log('[DEBUG] Set userRulesData to:', (report as any).dq_framework_results);
            return;
        }

        // Fallback: fetch rules for live analysis if dataObjectUuid is available
        if (!reportId && dataObjectUuid) {
            console.log('[DEBUG] Fetching rules for live analysis');
            fetchUserRules();
        } else {
            console.log('[DEBUG] No dq_framework_results found, setting userRulesData to null');
            setUserRulesData(null);
        }
    }, [report, reportId, dataObjectUuid]);

    // Fetch rules data for the current data object (for live analysis)
    const fetchUserRules = useCallback(async () => {
        if (!dataObjectUuid) return;

        try {
            setRulesLoading(true);
            const rulesResponse = await dqRuleService.getRulesForDataObject(dataObjectUuid);

            // Only set data if there are actual rules applied
            if (rulesResponse.rules && rulesResponse.rules.length > 0) {
                // Transform backend rules to expected format
                const transformedRules = {
                    column_results: rulesResponse.rules.map(rule => ({
                        column_name: rule.column_name,
                        rule_type: rule.rule_type,
                        pass_rate: 98.5, // TODO: Get from rule execution results when available
                        fail_count: 15,  // TODO: Get from rule execution results when available
                        breach_samples: [], // TODO: Get from rule execution results when available
                        total_records: 1000 // TODO: Get from data object metadata
                    }))
                };
                setUserRulesData(transformedRules);
            } else {
                setUserRulesData(null); // No rules applied
            }
        } catch (error) {
            console.warn('[NewReportPage] Failed to fetch user rules:', error);
            setUserRulesData(null);
        } finally {
            setRulesLoading(false);
        }
    }, [dataObjectUuid]);

    // Fetch user rules when data object UUID changes
    useEffect(() => {
        if (dataObjectUuid && !loading) {
            fetchUserRules();
        }
    }, [dataObjectUuid, loading, fetchUserRules]);

    // Show skeleton while loading initially
    if (loading && !report) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300">
                <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 flex items-center justify-center min-h-[400px]">
                    <div className="simple-spinner-large"></div>
                </div>
            </div>
        );
    }

    // Show error panel if initial load failed
    if (error && !report) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300">
                <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6">
                    <ReportErrorPanel error={error} onRetry={() => refresh()} />
                </div>
            </div>
        );
    }

    // Should not happen (loading would be true), but safety check
    if (!report) {
        return null;
    }

    return (
        <ErrorBoundary>
            <CinematicThemeProvider defaultVariant="vibrant">
                <div className={`min-h-screen ${themeClasses.pageBackground}`} data-export-target="true">
                    {/* Cinematic Sections */}
                    <div className="relative">
                        {/* Action Buttons Inside Report */}
                        <div className="fixed top-24 left-20 z-50 report-navigation-buttons flex items-center gap-3">
                            <button
                                onClick={handleBackNavigation}
                                className="inline-flex items-center justify-center px-5 py-3 text-white font-semibold rounded-full shadow-lg transition-all duration-200 whitespace-nowrap"
                                style={{
                                    background: 'linear-gradient(to right, #dc2626, #b91c1c, #991b1b)',
                                    boxShadow: '0 4px 14px -2px rgba(220, 38, 38, 0.4)'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'scale(1.015)';
                                    e.currentTarget.style.boxShadow = '0 6px 18px -2px rgba(220, 38, 38, 0.5)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = '';
                                    e.currentTarget.style.boxShadow = '0 4px 14px -2px rgba(220, 38, 38, 0.4)';
                                }}
                                aria-label="Return to data sources"
                            >
                                <svg className="mr-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                                {t('Back')}
                            </button>
                            <ContextualHelpLink videoId="tv-004" />
                        </div>

                        {/* Download & Share - top right */}
                        <div className="fixed top-24 right-6 z-50 report-navigation-buttons flex items-center gap-3">
                            {/* Download PDF */}
                            <button
                                type="button"
                                onClick={handleDownloadPDF}
                                disabled={isExporting}
                                className="inline-flex items-center justify-center w-12 h-12 text-white rounded-full shadow-lg transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                                style={{
                                    background: 'linear-gradient(to right, #dc2626, #b91c1c, #991b1b)',
                                    boxShadow: '0 4px 14px -2px rgba(220, 38, 38, 0.4)'
                                }}
                                onMouseEnter={(e) => {
                                    if (!isExporting) {
                                        e.currentTarget.style.transform = 'scale(1.015)';
                                        e.currentTarget.style.boxShadow = '0 6px 18px -2px rgba(220, 38, 38, 0.5)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = '';
                                    e.currentTarget.style.boxShadow = '0 4px 14px -2px rgba(220, 38, 38, 0.4)';
                                }}
                                aria-label={t('Download as PDF')}
                                title={t('Download as PDF')}
                            >
                                {isExporting ? (
                                    <svg className="animate-spin w-6 h-6" viewBox="0 0 24 24" fill="none">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                    </svg>
                                ) : (
                                    <IoIosCloudDownload className="w-6 h-6" />
                                )}
                            </button>

                            {/* Share Link */}
                            <button
                                type="button"
                                onClick={handleShareLink}
                                disabled={isSharing}
                                className="inline-flex items-center justify-center w-12 h-12 text-white rounded-full shadow-lg transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                                style={{
                                    background: 'linear-gradient(to right, #dc2626, #b91c1c, #991b1b)',
                                    boxShadow: '0 4px 14px -2px rgba(220, 38, 38, 0.4)'
                                }}
                                onMouseEnter={(e) => {
                                    if (!isSharing) {
                                        e.currentTarget.style.transform = 'scale(1.015)';
                                        e.currentTarget.style.boxShadow = '0 6px 18px -2px rgba(220, 38, 38, 0.5)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = '';
                                    e.currentTarget.style.boxShadow = '0 4px 14px -2px rgba(220, 38, 38, 0.4)';
                                }}
                                aria-label={t('Share with Link')}
                                title={t('Share with Link')}
                            >
                                {isSharing ? (
                                    <svg className="animate-spin w-6 h-6" viewBox="0 0 24 24" fill="none">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                    </svg>
                                ) : (
                                    <HiShare className="w-6 h-6" />
                                )}
                            </button>
                        </div>

                        {/* Scoped selection banner */}
                        {stateSelectedColumns.length > 0 && (
                            <div className="fixed top-24 right-6 z-50 max-w-xl bg-white/80 backdrop-blur-md border border-red-200 text-red-800 rounded-lg shadow px-4 py-3">
                                <div className="text-sm font-semibold">{t('Scoped to')} {stateSelectedColumns.length} {stateSelectedColumns.length > 1 ? t('selected columns') : t('selected column')}</div>
                                <div className="mt-1 text-xs text-red-700 truncate">
                                    {stateSelectedColumns.slice(0, 6).join(', ')}{stateSelectedColumns.length > 6 ? `, +${stateSelectedColumns.length - 6} more` : ''}
                                </div>
                            </div>
                        )}
                        {/* Data Report */}
                        <div data-report-content className="relative">
                            {/* Data Report */}
                            <CinematicExecutiveSummary
                                summary={displayReport.summary}
                                dataObjectDescription={displayReport.dataObjectDescription}
                                executiveSummary={displayReport.executiveSummary}
                                reasoningResult={displayReport.reasoningResult}
                                datasetName={displayReport.dataset.name}
                            />

                            {/* Health Score & Quality Dimensions Combined */}
                            <CinematicHealthAndDimensions
                                healthScore={displayReport.healthScore.overall}
                                healthDimensions={displayReport.healthScore.dimensions}
                                datasetMeta={{
                                    name: displayReport.dataset.name,
                                    rows: displayReport.dataset.rowCount,
                                    columns: displayReport.dataset.columnCount
                                }}
                                userRulesData={userRulesData}
                            />


                            {/* Section 3: Problems & Solutions */}
                            <CinematicProblemSolutions problems={displayReport.problems} />
                        </div>
                    </div>
                </div>
            </CinematicThemeProvider>

            {/* Floating Action Buttons - bottom right */}
            <div className="fixed bottom-6 right-6 z-40 flex items-center gap-3">
                {/* Language */}
                <button
                    type="button"
                    onClick={() => setShowLanguageModal(true)}
                    className="inline-flex items-center justify-center w-12 h-12 text-white rounded-full shadow-lg transition-all duration-200"
                    style={{
                        background: 'linear-gradient(to right, #dc2626, #b91c1c, #991b1b)',
                        boxShadow: '0 4px 14px -2px rgba(220, 38, 38, 0.4)'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.015)';
                        e.currentTarget.style.boxShadow = '0 6px 18px -2px rgba(220, 38, 38, 0.5)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = '';
                        e.currentTarget.style.boxShadow = '0 4px 14px -2px rgba(220, 38, 38, 0.4)';
                    }}
                    aria-label={t('Change Language')}
                    title={t('Change Language')}
                >
                    <IoLanguage className="w-6 h-6" />
                </button>

                <button
                    type="button"
                    onClick={() => setShowFeedbackModal(true)}
                    className="inline-flex items-center justify-center px-5 py-3 text-white font-semibold rounded-full shadow-lg transition-all duration-200 whitespace-nowrap"
                    style={{
                        background: 'linear-gradient(to right, #dc2626, #b91c1c, #991b1b)',
                        boxShadow: '0 4px 14px -2px rgba(220, 38, 38, 0.4)'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.015)';
                        e.currentTarget.style.boxShadow = '0 6px 18px -2px rgba(220, 38, 38, 0.5)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = '';
                        e.currentTarget.style.boxShadow = '0 4px 14px -2px rgba(220, 38, 38, 0.4)';
                    }}
                    aria-label="Send Feedback"
                >
                    <VscFeedback className="mr-2 w-5 h-5" />
                    {t('Feedback')}
                </button>
            </div>

            {/* Feedback Modal */}
            <FeedbackModal
                isOpen={showFeedbackModal}
                onClose={() => setShowFeedbackModal(false)}
                currentReportId={reportId}
                currentReportName={report?.dataset?.name}
            />

            {/* Language Modal */}
            <LanguageSelectorModal isOpen={showLanguageModal} onClose={() => setShowLanguageModal(false)} />

            {/* Share Link Popup */}
            {showSharePopup && (
                <div
                    className="fixed inset-0 z-[100000] flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                    onClick={() => setShowSharePopup(false)}
                >
                    <div
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-xl mx-4 p-8"
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <HiShare className="w-5 h-5" style={{ color: '#D31145' }} />
                                <h3 className="text-base font-semibold text-gray-900">{t('Share Report')}</h3>
                            </div>
                            <button
                                onClick={() => setShowSharePopup(false)}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                                aria-label="Close"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <p className="text-sm text-gray-500 mb-3">{t('Anyone with this link can view the report.')}</p>

                        {/* Link input + copy button */}
                        <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200">
                            <input
                                readOnly
                                value={shareUrl}
                                className="flex-1 bg-transparent text-sm text-gray-700 outline-none truncate"
                                onFocus={e => e.target.select()}
                            />
                            <button
                                onClick={handleCopyShareLink}
                                className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-all"
                                style={{ background: linkCopied ? '#16a34a' : 'linear-gradient(to right, #dc2626, #b91c1c)' }}
                            >
                                {linkCopied ? (
                                    <>
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                                        </svg>
                                        {t('Copied!')}
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                        {t('Copy')}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Live region for announcements */}
            <div
                role="status"
                aria-live="polite"
                className="sr-only"
                id="report-live-region"
            />
        </ErrorBoundary>
    );
}